// Global rules for every request the app makes to the backend.
//
// 1. Expired / invalid login  -> log the user out and send them to the login page
//    (previously the app silently showed empty pages until they logged out manually).
// 2. Server waking up (Render free plan) -> quietly retry a few times instead of
//    showing empty pages.
import axios from 'axios';
import { toast } from 'sonner';

const RETRY_DELAYS_MS = [3000, 6000, 10000, 15000]; // ~34 seconds in total
let wakeToastShown = false;

const clearSessionAndGoToLogin = () => {
  ['token', 'userId', 'userName', 'userEmail', 'isAdmin'].forEach(k => localStorage.removeItem(k));
  sessionStorage.setItem('sessionExpired', '1');
  if (window.location.pathname !== '/login') window.location.href = '/login';
};

axios.interceptors.response.use(
  (response) => {
    if (wakeToastShown) { toast.dismiss('server-waking'); wakeToastShown = false; }
    return response;
  },
  async (error) => {
    const config = error.config || {};
    const status = error.response?.status;
    const isLoginCall = (config.url || '').includes('/auth/login');

    // 1. Login no longer valid
    if (status === 401 && !isLoginCall) {
      clearSessionAndGoToLogin();
      return Promise.reject(error);
    }

    // 2. Server asleep / restarting: no response at all, or 502/503/504
    const serverWaking = !error.response || [502, 503, 504].includes(status);
    config._retryCount = config._retryCount || 0;
    if (serverWaking && config._retryCount < RETRY_DELAYS_MS.length) {
      if (!wakeToastShown) {
        toast.loading('Waking up the server, please wait a few seconds…', { id: 'server-waking', duration: 40000 });
        wakeToastShown = true;
      }
      const delay = RETRY_DELAYS_MS[config._retryCount];
      config._retryCount += 1;
      await new Promise(r => setTimeout(r, delay));
      return axios(config);
    }

    if (wakeToastShown) { toast.dismiss('server-waking'); wakeToastShown = false; }
    return Promise.reject(error);
  }
);
